import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Copy, Award, Calendar as CalIcon, Star } from "lucide-react";
import { ProfileHeader } from "../components/ProfileHeader";
import { ClientGallery } from "../components/ClientGallery";
import { WaitlistNotice } from "../components/WaitlistNotice";
import { RewardCoupons } from "../components/RewardCoupons";

const STATUS_STYLES = {
  confirmed: "bg-[rgba(74,124,62,0.12)] text-[color:var(--success)]",
  completed: "bg-[rgba(199,154,58,0.15)] text-[color:var(--sun-700)]",
  cancelled: "bg-[rgba(179,58,58,0.12)] text-[color:var(--danger)]",
  pending: "bg-[rgba(122,105,66,0.12)] text-[color:var(--ink-500)]",
};
const STATUS_LABEL = { confirmed:"Confirmado", completed:"Concluído", cancelled:"Cancelado", pending:"Pendente" };

function hoursDiff(iso) {
  return (new Date(iso).getTime() - Date.now()) / (1000*3600);
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [apts, setApts] = useState([]);
  const [ref, setRef] = useState(null);
  const [tiers, setTiers] = useState([]);
  useEffect(() => { api.get("/loyalty").then(r => setTiers(r.data.tiers)).catch(() => {}); }, []);

  const load = () => {
    api.get("/appointments/mine").then(r=> setApts(r.data));
    api.get("/referrals/mine").then(r=> setRef(r.data));
    refresh();
  };
  useEffect(load, []);

  const cancel = async (id, iso) => {
    if (hoursDiff(iso) < 48) {
      toast.error("Cancelamento requer 48h de antecedência. Fale via WhatsApp.");
      return;
    }
    try {
      await api.post(`/appointments/${id}/cancel`);
      toast.success("Agendamento cancelado.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao cancelar");
    }
  };

  if (!user) return null;

  const visits = user.visit_count || 0;
  const stampsMax = Math.max(10, ...tiers.map(t => t.visits));
  const nextMilestone = (tiers.find(t => t.visits > visits) || {}).visits || (visits + 5);

  const copyCode = () => {
    navigator.clipboard.writeText(user.referral_code || "");
    toast.success("Código copiado!");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <WaitlistNotice />
      <RewardCoupons />
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Overview + Loyalty */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-arya p-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <ProfileHeader user={user} onSaved={refresh}/>
              <div className="text-right">
                <p className="text-xs tracking-widest text-[color:var(--sun-700)] font-display">VISITAS</p>
                <p className="font-serif text-5xl text-[color:var(--sun-700)]">{visits}</p>
                <p className="text-xs text-[color:var(--ink-500)]">próximo brinde em {Math.max(0, nextMilestone - visits)} visita(s)</p>
              </div>
            </div>

            <div className="mt-8">
              <p className="text-xs font-display tracking-widest text-[color:var(--sun-700)] mb-3">CARTÃO DE FIDELIDADE DIGITAL</p>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-3">
                {Array.from({length: stampsMax}).map((_,i)=>(
                  <div key={i} className={`stamp ${i < visits ? 'filled':''}`} data-testid={`stamp-${i}`}>
                    {i < visits ? <Star size={16}/> : i+1}
                  </div>
                ))}
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm" data-testid="loyalty-tiers">
                {tiers.map(t => (
                  <div key={t.visits} className="rounded-xl border border-[var(--line)] p-4 bg-white/60">
                    <div className="flex items-center gap-2 font-display font-semibold"><Award size={16} className="text-[color:var(--sun-600)]"/>{t.visits} visitas</div>
                    <p className="text-[color:var(--ink-500)] text-xs mt-1">{t.reward}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card-arya p-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><CalIcon size={16} className="text-[color:var(--sun-600)]"/><h2 className="font-serif text-2xl">Meus agendamentos</h2></div>
              <Link to="/agendar" className="btn-gold !py-2 !px-4 text-sm" data-testid="btn-new-booking">Novo agendamento</Link>
            </div>
            {apts.length === 0 && <p className="text-sm text-[color:var(--ink-500)]">Você ainda não possui agendamentos.</p>}
            <ul className="divide-y divide-[var(--line)]">
              {apts.map(a => {
                const hrs = hoursDiff(a.start_at);
                const canCancel = a.status === "confirmed" && hrs >= 48;
                return (
                  <li key={a.appointment_id} className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`apt-${a.appointment_id}`}>
                    <div>
                      <p className="font-display font-semibold">{a.service_title}</p>
                      <p className="text-xs text-[color:var(--ink-500)]">
                        {new Date(a.start_at).toLocaleString("pt-BR", { dateStyle: "full", timeStyle: "short" })} · {a.duration_min} min · R$ {a.price.toFixed(2).replace('.',',')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-3 py-1 rounded-full ${STATUS_STYLES[a.status] || ''}`}>{STATUS_LABEL[a.status] || a.status}</span>
                      {a.status === "confirmed" && (
                        canCancel ? (
                          <button onClick={()=>cancel(a.appointment_id, a.start_at)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`btn-cancel-${a.appointment_id}`}>Cancelar</button>
                        ) : (
                          <a href={`https://wa.me/5521992483417?text=Ol%C3%A1%2C%20preciso%20cancelar%20meu%20agendamento%20${a.appointment_id}`}
                             target="_blank" rel="noreferrer"
                             className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`btn-wa-${a.appointment_id}`}>
                            Prazo 48h expirado · WhatsApp
                          </a>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="card-arya p-8"><ClientGallery /></div>
        </div>

        {/* Referral */}
        <div className="space-y-6">
          <div className="card-arya p-6">
            <div className="tag-sun mb-3 inline-block">INDIQUE & GANHE</div>
            <h3 className="font-serif text-2xl">Seu código pessoal</h3>
            <div className="mt-4 rounded-xl border border-dashed border-[color:var(--sun-500)] p-4 flex items-center justify-between bg-[rgba(233,204,133,.08)]">
              <span className="font-mono text-lg tracking-widest text-[color:var(--sun-700)]" data-testid="referral-code">{user.referral_code}</span>
              <button onClick={copyCode} className="btn-ghost !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="btn-copy-referral"><Copy size={12}/> Copiar</button>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-[color:var(--ink-500)]">Pontos acumulados</span>
              <span className="font-serif text-2xl text-[color:var(--sun-700)]" data-testid="referral-points">{ref?.points ?? user.referral_points ?? 0}</span>
            </div>
            <ul className="mt-3 text-xs text-[color:var(--ink-500)] space-y-1">
              <li>+20 pts ao cadastrar amiga com seu código</li>
              <li>+50 pts quando ela realiza o 1º serviço</li>
              <li>A campeã do mês ganha um cupom de prêmio exclusivo</li>
            </ul>
            <Link to="/indique" className="btn-gold w-full block text-center mt-4" data-testid="btn-see-ranking">Ver ranking</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
